import { useEffect, useMemo, useState } from "react";
import { ChevronRight, LogOut, Plus, Search, Bell } from "lucide-react";
import { Toaster, toast } from "sonner";
import { api } from "./lib/api";
import { nav, RESOURCE_KEYS } from "./lib/nav";
import Login from "./components/Login";
import CreateModal from "./components/CreateModal";
import Dashboard from "./views/Dashboard";
import ListView from "./views/ListView";
import Analytics from "./views/Analytics";
import Audit from "./views/Audit";
import Ledger from "./views/Ledger";
import IssueDialog from "./views/IssueDialog";
import "@/App.css";

function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [page, setPage] = useState("dashboard");
  const [dashboard, setDashboard] = useState({});
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [indents, setIndents] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [audit, setAudit] = useState([]);
  const [company, setCompany] = useState(null);
  const [modal, setModal] = useState(null);
  const [issueIndent, setIssueIndent] = useState(null);
  const [ledgerVendor, setLedgerVendor] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {}).finally(() => setChecking(false));
  }, []);

  const reloadAll = async () => {
    const [d, i, v, dep, ind, r, a, co] = await Promise.all([
      api.get("/dashboard").then((r) => r.data).catch(() => ({})),
      api.get("/inventory").then((r) => r.data).catch(() => []),
      api.get("/vendors").then((r) => r.data).catch(() => []),
      api.get("/departments").then((r) => r.data).catch(() => []),
      api.get("/indents").then((r) => r.data).catch(() => []),
      api.get("/receiving").then((r) => r.data).catch(() => []),
      api.get("/audit").then((r) => r.data).catch(() => []),
      api.get("/settings/company").then((r) => r.data).catch(() => null),
    ]);
    setDashboard(d); setItems(i); setVendors(v); setDepartments(dep); setIndents(ind); setReceipts(r); setAudit(a); setCompany(co);
  };

  useEffect(() => {
    if (user) reloadAll();
  }, [user]);

  const create = async (path, payload) => {
    try {
      await api.post(`/${path}`, payload);
      setModal(null);
      toast.success("Record saved");
      reloadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const issueSubmit = async (indentId, form) => {
    try {
      await api.post(`/indents/${indentId}/issue`, form);
      setIssueIndent(null);
      toast.success("Indent issued and delivered");
      reloadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to issue");
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
  };

  const current = nav.find((n) => n.id === page) || nav[0];
  const rowsForPage = useMemo(() => {
    const map = { inventory: items, vendors, departments, indents, receiving: receipts, audit };
    const rows = map[page] || [];
    if (!query) return rows;
    const q = query.toLowerCase();
    return (Array.isArray(rows) ? rows : []).filter((x) => JSON.stringify(x).toLowerCase().includes(q));
  }, [page, items, vendors, departments, indents, receipts, audit, query]);

  if (checking) return <div className="loading-screen">Loading MediStore…</div>;
  if (!user) return <Login onLogin={setUser} />;

  return (
    <div className="app-shell">
      <Toaster richColors position="top-right" />
      <aside className="sidebar">
        <div className="brand-row side-brand">
          <div className="brand-mark">+</div>
          <div><strong>MediStore</strong><span>Hospital inventory</span></div>
        </div>
        <div className="workspace-label">STORE WORKSPACE</div>
        <nav>
          {nav.map((n) => {
            const count = n.id === "indents" ? indents.filter((x) => x.status !== "Delivered").length : 0;
            return (
              <button
                key={n.id}
                className={page === n.id ? "nav-item active" : "nav-item"}
                data-testid={`nav-${n.id}-button`}
                onClick={() => { setPage(n.id); setQuery(""); setLedgerVendor(null); }}
              >
                <n.icon size={18} />
                <span>{n.label}</span>
                {count > 0 && <b>{count}</b>}
              </button>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <div className="sync-status"><span className="pulse-dot" />Data synced just now</div>
          <button className="nav-item" data-testid="logout-button" onClick={logout}>
            <LogOut size={18} />Sign out
          </button>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <div className="breadcrumbs">
            <span>MediStore</span>
            <ChevronRight size={14} />
            <strong>{current.label}</strong>
          </div>
          <div className="top-actions">
            <div className="search-wrap">
              <Search size={16} />
              <input data-testid="global-search-input" placeholder="Search records…" value={query} onChange={(e) => setQuery(e.target.value)} />
            </div>
            <button className="icon-btn notification" data-testid="notifications-button"><Bell size={18} /><i /></button>
            <div className="avatar" data-testid="user-avatar">SI</div>
          </div>
        </header>

        <div className="content">
          <div className="page-heading">
            <div>
              <p className="eyebrow">
                {page === "dashboard"
                  ? new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }).toUpperCase()
                  : "STORE WORKSPACE"}
              </p>
              <h1>{current.label}</h1>
              <p className="muted">
                {page === "dashboard" ? "A clear view of stock, fulfilment, and what needs your attention." : `Manage ${current.label.toLowerCase()} across the hospital store.`}
              </p>
            </div>
            {RESOURCE_KEYS.includes(page) && (
              <button className="primary" data-testid={`add-${page}-button`} onClick={() => setModal(page)}>
                <Plus size={17} /> Add {page === "indents" ? "indent" : page === "inventory" ? "item" : page === "receiving" ? "receipt" : page.slice(0, -1)}
              </button>
            )}
          </div>

          {page === "dashboard" && <Dashboard data={dashboard} onNavigate={setPage} />}
          {page === "analytics" && <Analytics />}
          {page === "audit" && <Audit data={rowsForPage} />}
          {page === "ledger" && (
            <Ledger vendors={vendors} initialVendor={ledgerVendor} onRefresh={reloadAll} />
          )}
          {RESOURCE_KEYS.includes(page) && (
            <ListView
              page={page}
              rows={rowsForPage}
              company={company}
              onOpenIndent={setIssueIndent}
              onOpenVendor={(v) => { setLedgerVendor(v); setPage("ledger"); }}
            />
          )}
        </div>
      </main>

      {modal && (
        <CreateModal
          type={modal}
          close={() => setModal(null)}
          create={create}
          catalogue={{ vendors, departments, items }}
        />
      )}
      {issueIndent && (
        <IssueDialog indent={issueIndent} onClose={() => setIssueIndent(null)} onSubmit={issueSubmit} />
      )}
    </div>
  );
}

export default App;
